import { motion } from "framer-motion";
import { CheckCircle2, FileCheck2, Info } from "lucide-react";
import { Reveal, SectionLabel, staggerContainer, staggerItem } from "../../lib/motion";
import { DUE_DILIGENCE, CTA_LABEL } from "../../data";

const RESALE_DISCLOSURES = [
  "Current HOA assessments",
  "Any outstanding balances or special assessments",
  "Governing documents and applicable rules",
  "Association disclosures required for closing",
];

export const DueDiligence = ({ onRequestPackage }) => {
  return (
    <section id="due-diligence" className="py-20 md:py-24 bg-white scroll-mt-20">
      <div className="max-w-3xl mx-auto px-6 md:px-10">
        <Reveal className="text-center">
          <div className="inline-flex items-center gap-2 bg-forest/10 text-forest rounded-full px-4 py-1.5 mb-5">
            <FileCheck2 size={16} />
            <span className="label-caps text-[11px]">Prepared for Buyer Review</span>
          </div>
          <SectionLabel className="block">Seller Due Diligence</SectionLabel>
          <h2 className="font-serif text-4xl md:text-5xl font-light text-navy mt-4 leading-[1.05]">
            Seller Due Diligence Completed
          </h2>
          <p className="mt-5 text-warm leading-relaxed">
            A comprehensive due diligence package has been assembled to help prospective buyers
            evaluate the property efficiently and transparently.
          </p>
        </Reveal>

        <motion.div
          variants={staggerContainer}
          initial="hidden"
          whileInView="show"
          viewport={{ once: true, margin: "-60px" }}
          className="mt-10 bg-white border border-warm-border rounded-3xl shadow-[0_20px_60px_-28px_rgba(11,37,69,0.25)] p-7 md:p-9"
        >
          <div className="space-y-3">
            {DUE_DILIGENCE.map((item, i) => (
              <motion.div
                key={i}
                variants={staggerItem}
                data-testid={`due-diligence-item-${i}`}
                className="flex items-center gap-4 border-b border-warm-border/60 last:border-0 pb-3 last:pb-0"
              >
                <CheckCircle2 size={22} className="text-water shrink-0" />
                <span className="text-navy font-medium leading-snug">{item}</span>
              </motion.div>
            ))}
          </div>
          <p className="mt-6 pt-5 border-t border-warm-border text-warm leading-relaxed text-center text-sm">
            A complete Due Diligence Package containing these documents is available upon request to
            qualified prospective buyers.
          </p>
        </motion.div>

        <Reveal delay={0.04}>
          <p data-testid="dd-pricing-note" className="mt-6 text-center text-warm leading-relaxed text-sm max-w-2xl mx-auto">
            The asking price reflects the property’s current condition, documented history, available
            due diligence materials, and recent comparable land sales within the area.
          </p>
        </Reveal>

        <Reveal delay={0.05}>
          <div
            data-testid="hoa-resale-callout"
            className="mt-6 bg-water-pale/60 border border-water/15 rounded-3xl p-7 md:p-9"
          >
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-full bg-water/10 flex items-center justify-center shrink-0">
                <Info size={20} className="text-water" />
              </div>
              <h3 className="font-serif text-2xl text-navy">Official HOA Resale Certificate</h3>
            </div>
            <div className="mt-5 space-y-4 text-warm leading-relaxed text-sm">
              <p>
                The property is located within the Bay Quarter Shores Property Owners Association, a
                mandatory homeowners association.
              </p>
              <p>
                An official Virginia Common Interest Community Association Resale Certificate will be
                ordered through the HOA’s management company and provided to the purchaser during the
                contract period, in accordance with Virginia law.
              </p>
              <p className="text-navy font-medium">This certificate contains the association’s current disclosures, including:</p>
              <ul className="space-y-2">
                {RESALE_DISCLOSURES.map((d) => (
                  <li key={d} className="flex items-start gap-3">
                    <span className="mt-2 w-1.5 h-1.5 rounded-full bg-water shrink-0" />
                    <span>{d}</span>
                  </li>
                ))}
              </ul>
              <p>
                This document is transaction-specific and is therefore obtained after a purchase
                agreement has been executed to ensure the buyer receives the most current information
                available.
              </p>
            </div>
          </div>
        </Reveal>

        <Reveal delay={0.1}>
          <div className="mt-10 text-center">
            <button
              data-testid="due-diligence-request-btn"
              onClick={onRequestPackage}
              className="bg-navy text-white rounded-full px-8 py-3.5 font-medium hover:bg-navy-light transition-colors shadow-sm hover:shadow-md focus-visible:ring-2 focus-visible:ring-water focus-visible:outline-none"
            >
              {CTA_LABEL}
            </button>
          </div>
        </Reveal>
      </div>
    </section>
  );
};
